import { type DataDogConfig, type SentryAppConfig, StimulusAppBootstrapper, track } from '@betterment/js-runtime';
import { registerSambaControllers } from '@betterment/samba-stimulus';
import { deferredTestTrack } from '@betterment/shared-components';
import * as TestTrack from '@betterment-oss/test-track';
import { type Definition } from '@hotwired/stimulus';
import * as Sentry from '@sentry/browser';
import { definitionsFromGlob } from 'stimulus-vite-helpers';

import appConfig from './src/utils/appConfig';

import type { AnalyticsProvider } from '@betterment-oss/test-track';

import './src/index.css';

const controllers = import.meta.glob(['./src/**/*-controller.ts', './src/**/*-controller.tsx'], { eager: true });
const definitions: Definition[] = definitionsFromGlob(controllers);

const sentryConfig: SentryAppConfig = {
  sentryApiToken: appConfig.sentryApiToken || '',
  sentryEnvironment: appConfig.sentryEnvironment || '',
  sentryRelease: appConfig.sentryRelease || '',
  isReplayEnabled: false,
  sentryErrorReplaySampleRate: 1,
  // @ts-expect-error TS2322 violates strict null checks
  user: appConfig.user,
  sentryWhitelistUrls: ['betterment.com', 'app.bettermentadvisorsolutions.com', 'clients.betterment.com'],
};

const dataDogConfig: DataDogConfig = {
  // @ts-expect-error TS2322 violates strict null checks
  applicationId: appConfig.dataDogApplicationId,
  // @ts-expect-error TS2322 violates strict null checks
  clientToken: appConfig.dataDogClientToken,
  env: appConfig.dataDogEnv || 'development',
  site: 'datadoghq.com',
  service: 'institutional',
  replaySampleRate: 0,
  isRumEnabled: Boolean(appConfig.dataDogEnabled) && appConfig.dataDogEnv === 'production',
  isLoggingEnabled: appConfig.dataDogEnv === 'production',
  allowedTracingUrls: [/https:\/\/*\.bettermentforadvisors\.com/, /https:\/\/*\.betterment\.com/],
};

const testTrackAnalytics: AnalyticsProvider = {
  trackAssignment(_visitorId, assignment) {
    track('SplitAssigned', {
      splitName: assignment.splitName,
      splitVariant: assignment.variant ?? '',
      splitContext: assignment.context ?? undefined,
    });
  },
  alias: () => {},
  identify: () => {},
};

export { appConfig };

export async function startStimulusApplication() {
  const testTrack = TestTrack.initialize({
    client: {
      appName: 'institutional',
      appVersion: '0.0.0',
      buildTimestamp: import.meta.env.TT_BUILD_TIMESTAMP as string,
    },
    analytics: testTrackAnalytics,
    errorLogger: message => Sentry.captureException(`test_track ${message}`),
  });
  deferredTestTrack.resolve(testTrack);

  const bootstrapper = new StimulusAppBootstrapper(definitions, sentryConfig, dataDogConfig);
  const application = await bootstrapper.initialize();
  registerSambaControllers(application);
}
